import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import { useIntl } from 'react-intl';
import {
  Modal, ModalBody, ModalHeader, Progress,
} from 'reactstrap';
import styled from 'styled-components';

const LoadProgress = styled(Progress).attrs({
  className: 'my-2 rounded-pill',
})`
  height: 1.5rem;
`;

const LoadProgressBar = styled(Progress).attrs({
  className: 'rounded-pill',
})``;

const StepCounter = styled.div.attrs({
  className: 'mb-2 text-muted small',
})`
  text-align: right;
`;

const PercentageDisplay = styled.div.attrs({
  className: 'mt-2 text-muted small',
})`
  text-align: right;
`;

const ProgressLabelText = styled.div.attrs({
  className: 'mt-2',
})``;

/**
 * @typedef {object} LoadingOverlayInputs
 * @property {string} bodyLabel - The text to display in the modal body.
 * @property {string} footerLabel - The text to display in the modal footer.
 * @property {string} headerLabel - The text to display in the modal header.
 * @property {string} [progressLabel] - The text to display below the progress bar.
 * @property {number} [progressBarValue=50] - The number of periods(chunks) already loaded.
 * @property {number} [currentStep] - The currently active step number.
 * @property {number} [totalSteps] - The total number of steps in the loading process.
 * @property {Function} updateProgress - A callback function to be called when the modal opens.
 */

/**
 * LoadingOverlay component displays a modal with a loading progress bar.
 * It is designed to block user interaction during a loading process.
 * @param {object} props - The component's props.
 * @param {LoadingOverlayInputs} props.inputs - An object containing all the inputs
 *  to configure the loading overlay.
 * @returns {React.ReactElement|null} The LoadingOverlay modal component or null.
 */
function LoadingOverlay({ inputs }) {
  if (!inputs || !inputs.headerLabel || !inputs.bodyLabel || !inputs.updateProgress) {
    return null;
  }
  // If the loaded value(periodsLoaded) is not present, the progress will be shown at the 50% range.
  const {
    bodyLabel, footerLabel, headerLabel, progressLabel,
    updateProgress, progressBarValue = 50,
    currentStep, totalSteps,
  } = inputs;
  const modalRef = useRef(null);
  const intl = useIntl();

  const handleModalOpened = () => {
    try {
      updateProgress(true);
    } catch (error) {
      console.error('Error in updateProgress callback:', error);
    }

    if (modalRef.current) {
      modalRef.current.focus();
    }
  };

  const stepCounterLabel = intl.formatMessage(
    {
      id: 'dashboard.loading_overlay.step_counter.label',
      defaultMessage: 'Step {currentStep} of {totalSteps}',
    },
    {
      currentStep,
      totalSteps,
    },
  );
  const showStepProgress = currentStep != null && totalSteps != null && totalSteps > 0;
  return (
    <Modal
      isOpen
      innerRef={modalRef}
      backdrop="static"
      keyboard={false}
      centered
      onOpened={handleModalOpened}
    >
      <ModalHeader>
        <h5 className="modal-title">{headerLabel}</h5>
      </ModalHeader>
      <ModalBody>
        <span>{bodyLabel}</span>
        {showStepProgress && (
          <StepCounter>{stepCounterLabel}</StepCounter>
        )}
        <LoadProgress multi>
          <LoadProgressBar
            animated
            bar
            value={progressBarValue}
            color="primary"
          />
        </LoadProgress>
        {showStepProgress && (
          <PercentageDisplay>
            {progressBarValue}
            %
          </PercentageDisplay>
        )}
        {progressLabel && <ProgressLabelText>{progressLabel}</ProgressLabelText>}
        {footerLabel && <p>{footerLabel}</p>}
      </ModalBody>
    </Modal>
  );
}

export default LoadingOverlay;

LoadingOverlay.propTypes = {
  inputs: PropTypes.shape({
    bodyLabel: PropTypes.string.isRequired,
    footerLabel: PropTypes.string,
    headerLabel: PropTypes.string.isRequired,
    progressBarValue: PropTypes.number,
    progressLabel: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
    ]),
    currentStep: PropTypes.number,
    totalSteps: PropTypes.number,
    updateProgress: PropTypes.func.isRequired,
  }),
};

LoadingOverlay.defaultProps = {
  inputs: {
    footerLabel: null,
    progressBarValue: 50,
    progressLabel: null,
    currentStep: null,
    totalSteps: null,
  },
};
